import React, { useState } from 'react';
import './Index.scss';
import useLocales from 'hooks/useLocales';
import CompetenciesSelect from 'components/shared/CompetenciesSelect';
import Button from 'components/Button/Index';
import Activation from '../Index';

interface Competency {
  id: string | number;
  name: string;
  description?: string;
}

interface CompetencyGroup {
  id: string | number;
  name: string;
  competencies: Competency[];
}

interface ActivationCultureProfileProps {
  competencies: CompetencyGroup[];
  onComplete: (data: {
    title: string;
    competencies: { id: string | number; name: string; score: number }[];
  }) => void;
  onBack?: () => void;
  currentStep?: number;
  totalSteps?: number;
}

const ActivationCultureProfile: React.FC<ActivationCultureProfileProps> = ({
  competencies,
  onComplete,
  onBack,
  currentStep,
  totalSteps
}) => {
  const { t } = useLocales();
  const [step, setStep] = useState(1);
  const [selectedList, setSelectedList] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [scores, setScores] = useState<{ [label: string]: number }>({});
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSetSelected = (label: string, id: string | number) => {
    const idx = selectedList.indexOf(label);
    const idxId = selectedIds.indexOf(id);
    if (idx !== -1 && idxId !== -1) {
      const newLabels = [...selectedList];
      const newIds = [...selectedIds];
      newLabels.splice(idx, 1);
      newIds.splice(idxId, 1);
      setSelectedList(newLabels);
      setSelectedIds(newIds);
    } else if (selectedList.length < 5 && selectedIds.length < 5) {
      setSelectedList([...selectedList, label]);
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSave = () => {
    if (!title.trim()) {
      setError('Please enter a culture profile title.');
      return;
    }
    if (selectedList.length !== 5) {
      setError('Please select 5 competencies.');
      return;
    }
    setError(null);
    onComplete({
      title: title.trim(),
      competencies: selectedList.map((name, i) => ({
        id: selectedIds[i],
        name,
        score: scores[name] ?? 5
      }))
    });
  };

  const footer = (
    <div className="buttonContainer">
      {step === 1 ? (
        <>
          <Button
            className="back button"
            t={t}
            title={t('Back')}
            onClick={onBack}
          />
          <Button
            className="next button"
            t={t}
            title={t('Next')}
            onClick={() => setStep(2)}
            disabled={selectedList.length !== 5}
          />
        </>
      ) : (
        <>
          <Button
            className="back button"
            t={t}
            title={t('Select competencies')}
            onClick={() => setStep(1)}
          />
          <Button
            className="next button"
            t={t}
            title={t('Save')}
            onClick={handleSave}
          />
        </>
      )}
    </div>
  );

  return (
    <Activation
      title={t('Create your culture profile')}
      sub_title={t('Select 5 competencies and set their importance.')}
      currentStep={currentStep}
      totalSteps={totalSteps}
      style={{ padding: '40px' }}
    >
      <div className="competencies-popup-content">
        <div className="competencies-popup-scrollable">
          <CompetenciesSelect
            step={step}
            setStep={setStep}
            competencies={competencies}
            selectedList={selectedList}
            onSelect={handleSetSelected}
            scores={scores}
            setScores={setScores}
            formValues={{ matching: title }}
            setFormValues={({ matching }: any) => setTitle(matching)}
            fromCulture={true}
          />
          <div className="helper-text">
            {t('Select 5 competencies that best represent your company culture.')}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="competencies-popup-footer">{footer}</div>
      </div>
    </Activation>
  );
};

export default ActivationCultureProfile;
